/**
 * ShapefileImport — 3-step import flow for field polygons.
 *
 * Step 1: Upload (.zip shapefile, .geojson, .kml)
 * Step 2: Preview + edit (table with checkboxes, inline name/crop edit)
 * Step 3: Confirmation (success summary)
 *
 * Props:
 *   token    — JWT
 *   onDone   — () => void — called when import is complete or cancelled
 */
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const CROP_KEYS = [
  { value: '', labelKey: '' },
  { value: 'wheat', labelKey: 'crops.wheat' },
  { value: 'barley', labelKey: 'crops.barley' },
  { value: 'rapeseed', labelKey: 'crops.rapeseed' },
  { value: 'corn', labelKey: 'crops.corn' },
  { value: 'potato', labelKey: 'crops.potato' },
  { value: 'sugarbeet', labelKey: 'crops.sugarbeet' },
  { value: 'grassland', labelKey: 'crops.grassland' },
  { value: 'sunflower', labelKey: 'crops.sunflower' },
  { value: 'peas', labelKey: 'import.peas' },
  { value: 'fababean', labelKey: 'import.fababean' },
  { value: 'other', labelKey: 'import.other' },
];

export default function ShapefileImport({ token, onDone }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=done
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // API response
  const [fields, setFields] = useState([]); // editable field list
  const [selected, setSelected] = useState(new Set()); // selected indices
  const [result, setResult] = useState(null); // import result
  const fileRef = useRef(null);
  const dragRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  // ── Step 1: Upload ──────────────────────────────────────────────────────

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowed = ['zip', 'geojson', 'json', 'kml', 'shp'];
    if (!allowed.includes(ext)) {
      setError(t('import.unsupportedFormat', { ext }));
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const result = await apiClient.post('/api/v1/aif/farm/fields/import', form, {
        queueOnOffline: true,
        entityType: 'field-shapefile',
      });
      const data = result.data;
      setPreview(data);
      const allFields = data.preview.map((f, i) => ({ ...f, selected: true, editName: f.name, editCrop: f.current_crop || '' }));
      setFields(allFields);
      setSelected(new Set(allFields.map((_, i) => i)));
      setStep(2);
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  // ── Step 2: Preview ─────────────────────────────────────────────────────

  const toggleField = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const updateFieldName = (idx, name) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, editName: name } : f));
  };

  const updateFieldCrop = (idx, crop) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, editCrop: crop } : f));
  };

  const handleConfirm = async () => {
    setImporting(true);
    setError(null);
    try {
      const skipIndices = fields.map((_, i) => i).filter(i => !selected.has(i));
      const payload = {
        fields: fields.map(f => ({
          name: f.editName || f.name,
          polygon_geojson: f.polygon_geojson,
          area_ha: f.area_ha,
          current_crop: f.editCrop || null,
          flik_number: f.flik_number || null,
        })),
        skip_indices: skipIndices,
      };
      const result = await apiClient.post('/api/v1/aif/farm/fields/import/confirm', payload, {
        entityType: 'field-import-confirm',
      });
      const data = result.data;
      setResult(data);
      setStep(3);
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = selected.size;
  const selectedArea = fields.filter((_, i) => selected.has(i)).reduce((s, f) => s + (f.area_ha || 0), 0);

  const inputCls = `px-2 py-1.5 rounded-lg border border-[var(--aif-border-default)]
                    bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-xs
                    focus:outline-none focus-visible:ring-1 focus:ring-[var(--aif-color-primary)]`;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div data-testid="shapefile-import" className="flex flex-col h-full bg-[var(--aif-bg-page)]">

      {/* ── Step 1: Upload ─────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <h2 className="text-base font-semibold text-[var(--aif-text-primary)] mb-4">
            {t('import.title')}
          </h2>

          {error && (
            <div className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2 mb-4 max-w-sm text-center">
              {error}
            </div>
          )}

          <div
            ref={dragRef}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={`w-full max-w-sm border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition
              ${dragOver
                ? 'border-[var(--aif-color-primary)] bg-[var(--aif-color-primary)]/5'
                : 'border-[var(--aif-border-default)] hover:border-[var(--aif-color-primary)]'
              }`}
            data-testid="import-dropzone"
          >
            <div className="text-3xl mb-3">{uploading ? '⏳' : '📂'}</div>
            <p className="text-sm text-[var(--aif-text-primary)] font-medium mb-1">
              {uploading ? t('import.processing') : t('import.dragHere')}
            </p>
            <p className="text-xs text-[var(--aif-text-muted)] mb-3">
              {t('import.orClickToSelect')}
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {['.zip (Shapefile)', '.geojson', '.kml'].map(fmt => (
                <span key={fmt} className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]">
                  {fmt}
                </span>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".zip,.geojson,.json,.kml,.shp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="import-file-input"
            />
          </div>

          <div className="mt-4 max-w-sm text-center">
            <p className="text-[11px] text-[var(--aif-text-muted)] leading-relaxed">
              {t('import.shapefileHint')}
            </p>
          </div>

          <button
            onClick={onDone}
            className="mt-6 text-xs text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] transition"
          >
            {t('form.cancel')}
          </button>
        </div>
      )}

      {/* ── Step 2: Preview ────────────────────────────────────────────── */}
      {step === 2 && fields.length > 0 && (
        <>
          <div className="px-4 pt-4 pb-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-base font-semibold text-[var(--aif-text-primary)]">
                  {t('import.fieldsFound', { count: preview?.fields_found })}
                </h2>
                <p className="text-xs text-[var(--aif-text-muted)]">
                  {t('import.selectedSummary', { count: selectedCount, area: selectedArea.toFixed(1) })}
                </p>
              </div>
              <button
                onClick={() => { setStep(1); setPreview(null); setFields([]); setError(null); }}
                className="text-xs text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]"
              >
                {t('import.changeFile')}
              </button>
            </div>

            {error && (
              <div className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {fields.map((f, i) => (
              <div
                key={i}
                data-testid={`import-field-${i}`}
                className={`px-4 py-3 border-b border-[var(--aif-border-default)] transition
                  ${!selected.has(i) ? 'opacity-40' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggleField(i)}
                    className="mt-1 rounded flex-shrink-0"
                    data-testid={`import-check-${i}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <input
                        type="text"
                        value={f.editName}
                        onChange={(e) => updateFieldName(i, e.target.value)}
                        className={`flex-1 ${inputCls} font-medium`}
                        data-testid={`import-name-${i}`}
                      />
                      <span className="text-xs text-[var(--aif-text-muted)] whitespace-nowrap flex-shrink-0">
                        {f.area_ha} ha
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={f.editCrop}
                        onChange={(e) => updateFieldCrop(i, e.target.value)}
                        className={`flex-1 ${inputCls}`}
                      >
                        {CROP_KEYS.map(c => <option key={c.value} value={c.value}>{c.labelKey ? t(c.labelKey) : '—'}</option>)}
                      </select>
                      {f.flik_number && (
                        <span className="text-[10px] text-[var(--aif-text-muted)] whitespace-nowrap">
                          FLIK: {f.flik_number}
                        </span>
                      )}
                    </div>
                    {f.duplicate && (
                      <div className="mt-1.5 text-[10px] text-orange-500 flex items-center gap-1">
                        <span>⚠️</span>
                        <span>{t('import.duplicate', { name: f.duplicate.field_name, pct: f.duplicate.overlap_pct })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 py-3 border-t border-[var(--aif-border-default)] flex gap-3 flex-shrink-0">
            <button
              onClick={onDone}
              disabled={importing}
              className="flex-1 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                         text-[var(--aif-text-secondary)] text-sm font-medium
                         hover:bg-[var(--aif-bg-elevated)] transition disabled:opacity-50"
            >
              {t('form.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={importing || selectedCount === 0}
              className="flex-1 py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                         hover:opacity-90 transition disabled:opacity-50"
              data-testid="import-confirm-btn"
            >
              {importing ? t('import.importing') : t('import.importCount', { count: selectedCount })}
            </button>
          </div>
        </>
      )}

      {/* ── Step 3: Done ───────────────────────────────────────────────── */}
      {step === 3 && result && (
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="text-4xl mb-3">✅</div>
          <h2 className="text-base font-semibold text-[var(--aif-text-primary)] mb-2">
            {t('import.doneTitle', { count: result.imported_count })}
          </h2>
          <p className="text-sm text-[var(--aif-text-muted)] mb-6">
            {t('import.totalArea', { area: result.total_area_ha })}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onDone}
              className="px-4 py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                         hover:opacity-90 transition"
              data-testid="import-done-btn"
            >
              {t('import.toMap')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
